import clsx from "clsx";
import React from "react";

import styles from "./Toolbar.module.scss";

export type ToolbarProps = React.HTMLAttributes<HTMLDivElement>;

export type GroupProps = React.HTMLAttributes<HTMLDivElement>;

/**
 * Horizontal toolbar container.
 *
 * @dev The associated CSS module constrains the toolbar to a fixed height of 3.5rem
 * and enables horizontal scrolling with a hidden scrollbar.
 */
const DefaultToolbar = React.forwardRef<HTMLDivElement, ToolbarProps>(({ className, ...props }, ref) => (
  <div ref={ref} className={clsx(styles.toolbar, className)} {...props} />
));

/** Fixed-width spacer (1rem) for use inside a `Toolbar`. */
const Spacer = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={clsx(styles.spacer, className)} {...props} />
);
Spacer.displayName = "Toolbar.Spacer";

/** Flexible spacer that expands to fill remaining space inside a `Toolbar`. */
const FillSpace = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={clsx(styles.fillSpace, className)} {...props} />
);
FillSpace.displayName = "Toolbar.FillSpace";

/** Vertical divider for separating groups of items inside a `Toolbar`. */
const Divider = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={clsx(styles.divider, className)} role="separator" aria-orientation="vertical" {...props} />
);
Divider.displayName = "Toolbar.Divider";

/** Groups toolbar items with a small gap between them. */
const Group = React.forwardRef<HTMLDivElement, GroupProps>(({ className, ...props }, ref) => (
  <div ref={ref} className={clsx(styles.group, className)} {...props} />
));
Group.displayName = "Toolbar.Group";

const Toolbar = Object.assign(DefaultToolbar, {
  Spacer,
  FillSpace,
  Divider,
  Group,
});

export default Toolbar;
